import React from 'react';
import { BrushPanel } from './BrushPanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { Locale } from '../../i18n';
import { t } from '../../i18n';

interface HintsBarProps {
    hintsRef: React.RefObject<HTMLDivElement | null>;
    isBrushPanelOpen: boolean;
    setIsBrushPanelOpen: (isOpen: boolean) => void;
    brushSize: number;
    setBrushSize: (size: number) => void;
    brushPanelRef: React.RefObject<HTMLDivElement | null>;
    gravityEnabled: boolean;
    toggleGravity: () => void;
    simulateGravity: boolean;
    setSimulateGravity: (simulate: boolean) => void;
    setGravityEnabled: (enabled: boolean) => void;
    isLocalDev: boolean;
    locale: Locale;
}

export const HintsBar: React.FC<HintsBarProps> = ({
    hintsRef,
    isBrushPanelOpen,
    setIsBrushPanelOpen,
    brushSize,
    setBrushSize,
    brushPanelRef,
    gravityEnabled,
    toggleGravity,
    simulateGravity,
    setSimulateGravity,
    setGravityEnabled,
    isLocalDev,
    locale
}) => {
    const isMobile = useIsMobile();

    return (
        <div 
            ref={hintsRef}
            className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-slate-300/70 text-[11px] font-medium tracking-wide bg-black/20 px-4 py-1.5 rounded-full backdrop-blur-sm relative"
        >
            {!isMobile && (
                <div className="flex items-center gap-2">
                    <span className="bg-white/5 px-1.5 py-0.5 rounded text-slate-200/80 border border-white/10">{t(locale, "leftClick")}</span>
                    <span>{t(locale, "draw")}</span>
                </div>
            )}
            {!isMobile && <div className="w-px h-3 bg-white/10 self-center"></div>}
            <div 
                className={`flex items-center gap-2 cursor-pointer transition-colors ${isBrushPanelOpen ? 'text-teal-400' : 'hover:text-white'}`}
                onClick={() => setIsBrushPanelOpen(!isBrushPanelOpen)}
            >
                <span className={`px-1.5 py-0.5 rounded text-slate-200/80 border border-white/10 ${isBrushPanelOpen ? 'bg-teal-500/20 border-teal-500/30' : 'bg-white/5'}`}>{t(locale, "wheel")}</span>
                <span>{t(locale, "size")}: {brushSize * 2 + 1}</span>
            </div>
            {!isMobile && <div className="w-px h-3 bg-white/10 self-center"></div>}
            {!isMobile && (
                <div className="flex items-center gap-2">
                    <span className="bg-white/5 px-1.5 py-0.5 rounded text-slate-200/80 border border-white/10">{t(locale, "cmdCtrl")}</span>
                    <span>{t(locale, "line")}</span>
                </div>
            )}
            {!isMobile && <div className="w-px h-3 bg-white/10 self-center"></div>}
            {!isMobile && (
                <div className="flex items-center gap-2">
                    <span className="bg-white/5 px-1.5 py-0.5 rounded text-slate-200/80 border border-white/10">{t(locale, "cmdShift")}</span>
                    <span>{t(locale, "rect")}</span>
                </div>
            )}
            {!isMobile && <div className="w-px h-3 bg-white/10 self-center"></div>}
            <div 
                className={`flex items-center gap-2 cursor-pointer transition-colors ${gravityEnabled ? 'text-teal-400' : 'text-slate-200/80 hover:text-white'}`}
                onClick={toggleGravity}
            >
                <span className={`px-1.5 py-0.5 rounded text-slate-200/80 border ${gravityEnabled ? 'bg-teal-500/20 border-teal-500/30' : 'bg-white/5 border-white/10'}`}>
                    {gravityEnabled ? t(locale, "disableGravity") : t(locale, "enableGravity")}
                </span>
            </div>
            
            {isLocalDev && (
                <>
                    <div className="w-px h-3 bg-white/10 self-center hidden sm:block"></div>
                    <div 
                        className={`flex items-center gap-2 cursor-pointer transition-colors ${simulateGravity ? 'text-amber-400' : 'hover:text-white'}`}
                        onClick={() => {
                            setSimulateGravity(!simulateGravity);
                            // 互斥：开启模拟时关闭传感器重力
                            if (!simulateGravity) setGravityEnabled(false);
                        }}
                    >
                        <span className={`px-1.5 py-0.5 rounded text-slate-200/80 border border-white/10 ${simulateGravity ? 'bg-amber-500/20 border-amber-500/30' : 'bg-white/5'}`}>
                            {simulateGravity ? t(locale, "simMouse") : t(locale, "localSim")}
                        </span>
                    </div>
                </>
            )}

            <BrushPanel
                isOpen={isBrushPanelOpen}
                brushSize={brushSize}
                setBrushSize={setBrushSize}
                panelRef={brushPanelRef}
                locale={locale}
            />
        </div>
    );
};
